import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bell, CheckCircle2, XCircle, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

const iconFor = (k) => (k === "success" ? CheckCircle2 : k === "error" ? XCircle : Info);
const colorFor = (k) => (k === "success" ? "text-emerald-400" : k === "error" ? "text-rose-400" : "text-blue-400");

const INCOME_KW = ["bonus", "roi", "salary", "reward", "rank", "cycle", "pool", "target", "income", "commission"];
function categorize(n) {
  const t = (n.title || "").toLowerCase();
  if (t.includes("deposit")) return "deposits";
  if (t.includes("withdraw") || t.includes("payout")) return "withdrawals";
  if (INCOME_KW.some((k) => t.includes(k))) return "income";
  return "other";
}

const TABS = [
  { key: "all", label: "All" },
  { key: "deposits", label: "Deposits" },
  { key: "income", label: "Income" },
  { key: "withdrawals", label: "Withdrawals" },
];

function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    o.start(); o.stop(ctx.currentTime + 0.36);
    o.onended = () => ctx.close();
  } catch (e) { /* ignore */ }
}

function celebrate() {
  try {
    confetti({ particleCount: 130, spread: 78, startVelocity: 42, origin: { y: 0.25 }, zIndex: 9999 });
    setTimeout(() => confetti({ particleCount: 80, spread: 110, scalar: 0.9, origin: { y: 0.3 }, zIndex: 9999 }), 220);
  } catch (e) { /* ignore */ }
}

const isDepositConfirmed = (n) =>
  n.kind === "success" && /deposit/i.test(n.title || "") && /confirm|credited|approved/i.test(n.title || "");

export default function NotificationBell({ unread, setUnread, sound }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const lastSeenRef = useRef(null);
  const initRef = useRef(false);
  const wrapRef = useRef(null);
  const soundRef = useRef(sound);
  useEffect(() => { soundRef.current = sound; }, [sound]);

  useEffect(() => {
    let stop = false, timer;
    const poll = async () => {
      try {
        const { data } = await api.get("/user/notifications");
        if (stop) return;
        const list = data.notifications || [];
        setItems(list);
        setUnread(data.unread);
        const latest = list[0]?.created_at || null;
        if (!initRef.current) {
          initRef.current = true; lastSeenRef.current = latest;
        } else if (latest && latest !== lastSeenRef.current) {
          const prevSeen = lastSeenRef.current;
          const fresh = list.filter((n) => !prevSeen || n.created_at > prevSeen);
          lastSeenRef.current = latest;
          let played = false;
          fresh.slice(0, 3).reverse().forEach((n) => {
            if (isDepositConfirmed(n)) {
              const m = (n.body || "").match(/([\d,]+(?:\.\d+)?)\s*USDT/i);
              const amt = m ? m[1] : null;
              toast.success("🎉 Deposit Confirmed!", {
                description: amt ? `$${amt} USDT credited to your wallet` : n.body,
                duration: 6000,
              });
              celebrate();
              if (soundRef.current) { beep(); }
              played = true;
            } else {
              const fn = n.kind === "error" ? toast.error : n.kind === "success" ? toast.success : toast;
              fn(n.title, { description: n.body });
              if (n.kind === "success" && soundRef.current && !played) { beep(); played = true; }
            }
          });
        }
      } catch (e) { /* keep retrying quietly */ }
      timer = setTimeout(poll, 15000);
    };
    poll();
    return () => { stop = true; clearTimeout(timer); };
  }, []); // eslint-disable-line

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = async () => {
    const next = !open; setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      try { await api.post("/user/notifications/read"); } catch (e) { /* ignore */ }
    }
  };

  const filtered = useMemo(
    () => (tab === "all" ? items : items.filter((n) => categorize(n) === tab)),
    [items, tab]
  );

  return (
    <div className="relative" ref={wrapRef}>
      <button onClick={toggle} className="relative grid h-9 w-9 place-items-center rounded-xl glass-2 text-white" title="Notifications" data-testid="notif-bell">
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-[var(--gold)] px-1 text-[10px] font-bold text-black" data-testid="notif-bell-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-white/10 bg-[var(--bg)]/95 shadow-2xl backdrop-blur-xl" data-testid="notif-dropdown">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <span className="text-sm font-semibold text-white">Notifications</span>
            <button onClick={() => { setOpen(false); nav("/app/notifications"); }} className="text-xs text-[var(--gold)] hover:underline" data-testid="notif-view-all">View all</button>
          </div>
          <div className="flex items-center gap-1 border-b border-white/10 px-2 py-2">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={cn(
                  "flex-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold transition-colors",
                  tab === t.key ? "bg-[var(--gold)] text-black" : "text-[var(--text-dim)] hover:bg-white/5"
                )}
                data-testid={`notif-tab-${t.key}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-[var(--text-dim)]" data-testid="notif-empty">No notifications here</div>
            ) : filtered.slice(0, 12).map((n, i) => {
              const Icon = iconFor(n.kind);
              return (
                <button key={i} type="button" onClick={() => n.url && window.open(n.url, "_blank", "noopener,noreferrer")}
                  className={cn("flex w-full items-start gap-3 border-b border-white/5 px-4 py-3 text-left last:border-0", n.url && "cursor-pointer hover:bg-white/5")}
                  data-testid={`notif-item-${i}`}>
                  <Icon size={16} className={cn("mt-0.5 shrink-0", colorFor(n.kind))} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{n.title}</p>
                    <p className="text-xs text-[var(--text-dim)]">{n.body}</p>
                    <p className="mt-0.5 text-[10px] text-[var(--text-dim)]">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
